import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { companyData } from '../mock';
import { Code, Briefcase, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Services = () => {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <HeroSection />

      {/* Technology Services */}
      <TechnologyServices />

      {/* Business Services */}
      <BusinessServices />

      {/* CTA Section */}
      <CTASection />
    </div>
  );
};

const HeroSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <section ref={ref} className="py-24 bg-gradient-to-br from-[#004C97] to-[#003366] text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            Our Services
          </h1>
          <p className="text-xl md:text-2xl text-[#D4EDF7] max-w-3xl mx-auto leading-relaxed">
            Comprehensive IT and business consulting services tailored for the insurance industry
          </p>
        </motion.div>
      </div>
    </section>
  );
};

const TechnologyServices = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-[#D4EDF7] rounded-2xl flex items-center justify-center">
              <Code className="text-[#004C97]" size={32} />
            </div>
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#004C97]">
                Technology & Digital
              </h2>
              <p className="text-gray-600 text-lg mt-2">Cutting-edge technology solutions for modern insurance</p>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {companyData.services.technology.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} isInView={isInView} />
          ))}
        </div>
      </div>
    </section>
  );
};

const BusinessServices = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-24 bg-[#D4EDF7]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center">
              <Briefcase className="text-[#004C97]" size={32} />
            </div>
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#004C97]">
                Business & Operations
              </h2>
              <p className="text-gray-700 text-lg mt-2">Strategic business solutions to optimize your operations</p>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {companyData.services.business.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} isInView={isInView} variant="business" />
          ))}
        </div>
      </div>
    </section>
  );
};

const ServiceCard = ({ service, index, isInView, variant = 'tech' }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ 
        y: -10,
        boxShadow: '0 20px 40px rgba(0,76,151,0.2)'
      }}
      className={`${
        variant === 'business' ? 'bg-white' : 'bg-white'
      } p-8 rounded-2xl shadow-lg group cursor-pointer`}
    >
      <div className="mb-4">
        <motion.div
          initial={{ width: '0%' }}
          whileHover={{ width: '100%' }}
          className="h-1 bg-gradient-to-r from-[#004C97] to-[#003366] rounded-full"
        />
      </div>
      <h3 className="text-xl font-bold text-[#004C97] mb-4 group-hover:text-[#003366] transition-colors">
        {service.title}
      </h3>
      <p className="text-gray-600 leading-relaxed">
        {service.description}
      </p>
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        whileHover={{ opacity: 1, x: 0 }}
        className="flex items-center gap-2 mt-6 text-[#004C97] font-semibold"
      >
        Learn More <ArrowRight size={18} />
      </motion.div>
    </motion.div>
  );
};

const CTASection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <section ref={ref} className="py-24 bg-white">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={isInView ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 0.8 }}
        className="max-w-4xl mx-auto px-6 lg:px-8 text-center"
      >
        <h2 className="text-4xl md:text-6xl font-bold text-[#004C97] mb-6">
          Let's Build Something Great Together
        </h2>
        <p className="text-xl text-gray-700 mb-10 leading-relaxed">
          Discover how our specialized services can transform your insurance operations
        </p>
        <Link to="/contact">
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(0,76,151,0.3)' }}
            whileTap={{ scale: 0.98 }}
            className="px-10 py-5 bg-[#004C97] text-white rounded-lg font-semibold text-lg flex items-center gap-3 mx-auto shadow-xl hover:bg-[#003366] transition-colors"
          >
            Get Started
            <ArrowRight size={24} />
          </motion.button>
        </Link>
      </motion.div>
    </section>
  );
};

export default Services;